'use client';

import {
  type FC,
  useEffect,
  useState,
  useRef,
  useMemo,
  useCallback,
} from 'react';

import { pdf } from '@react-pdf/renderer';
import {
  Button,
  Col,
  Flex,
  Form,
  Input,
  Modal,
  Row,
  Space,
  Typography,
  Upload,
  message,
  Select,
} from 'antd';
import type { FormInstance } from 'antd/lib';
import { toJpeg } from 'html-to-image';
import type { JSX } from 'react/jsx-runtime';
import ReactCountryFlag from 'react-country-flag';
import { useTranslation } from 'react-i18next';
import useSWR from 'swr';

import { apiV1 } from '@/api/instances';
import GenerationCard from '@/components/RSimReport/components/GenerationCard';
import Table from '@/components/RTable';
import { AnimatedText } from '@/components/RTextAnimation';
import useCurrency from '@/contexts/useCurrency';
import useAuth from '@/hooks/useAuth';
import { useLanguage } from '@/hooks/useLanguage';
import { useOrganizationCustomization } from '@/hooks/useOrganizationCustomization';
import AnalysisTable from '@/pages/Result/Analysis/AnalysisTable';
import AnnualEnergyFlow from '@/pages/Result/Energy/AnnualEnergyFlow';
import BatteryAndLifeCycle from '@/pages/Result/Energy/BatteryAndLifeCycle';
import InfoCard from '@/pages/Result/Energy/BatteryAndLifeCycle/InfoCard';
import BatteryFlowForCharge from '@/pages/Result/Energy/BatteryFlowForCharge';
import BatteryFlowForDischarge from '@/pages/Result/Energy/BatteryFlowForDischarge';
import EnergyActivity from '@/pages/Result/Energy/EnergyActivity';
import EnergyActivityAtMarket from '@/pages/Result/Energy/EnergyActivityAtMarket';
import CashFlow from '@/pages/Result/Finance/CashFlow';
import CashFlowTable from '@/pages/Result/Finance/CashFlowTable';
import Ebitda from '@/pages/Result/Finance/Ebitda';
import IncomeBreakdown from '@/pages/Result/Finance/IncomeBreakdown';
import ProfitAndLossTableCompact from '@/pages/Result/Finance/ProfilAndLossTableCompact';
import KeyMetrics from '@/pages/Result/Summary/KeyMetrics';
import KeyMetricsTable from '@/pages/Result/Summary/KeyMetricsTable';
import type { AnnualGenerationGraph } from '@/types/plant';
import type { ComparisonChartData, ReportData } from '@/types/report';
import type { EnergyFlow } from '@/types/result';
import type {
  SimulationBatteryDegradation,
  SimulationResult,
  SimulationResultDetails,
  SimulationResultMarketEnergyActivity,
  SimulationResultYearlyFinancialAnalysis,
} from '@/types/simulations';
import { handleError } from '@/utils';

import ComparisonChart from './../components/ComparisonChart';
import Parameters from './../components/Parameters';
import SimulationReportDocument from './SimulationReport';
import {
  getUniqueIds,
  getAfterStorageTableColumns,
  getMemoizedTableData,
  getBeforeStorageTableColumns,
} from './tableUtils';

interface CreateReportProps {
  form: FormInstance;
}

const CreateReport: FC<CreateReportProps> = ({ form }) => {
  const { t } = useTranslation();
  const { currencySymbol } = useCurrency();
  const { user } = useAuth();
  const { customization } = useOrganizationCustomization(user?.organization.id);

  const chartRef = useRef<HTMLDivElement>(null);
  const metricsTableRef = useRef<HTMLDivElement>(null);
  const metricsRef = useRef<HTMLDivElement>(null);
  const batteryChargeRef = useRef<HTMLDivElement>(null);
  const batteryDischargeRef = useRef<HTMLDivElement>(null);
  const incomeBreakdownRef = useRef<HTMLDivElement>(null);
  const analysisTableRef = useRef<HTMLDivElement>(null);
  const ancillaryTableRef = useRef<HTMLDivElement>(null);
  const ebitdaStorageRef = useRef<HTMLDivElement>(null);
  const ebitdaWithStorageRef = useRef<HTMLDivElement>(null);
  const cashFlowWithStorageRef = useRef<HTMLDivElement>(null);
  const cashFlowStorageRef = useRef<HTMLDivElement>(null);
  const beforeStorageTableRef = useRef<HTMLDivElement>(null);
  const generationCardRef = useRef<HTMLDivElement>(null);
  const mcpChartRef = useRef<HTMLDivElement>(null);
  const pfcpChartRef = useRef<HTMLDivElement>(null);
  const sfcpChartRef = useRef<HTMLDivElement>(null);
  const energyActivityRef = useRef<HTMLDivElement>(null);
  const energyActivityAtMarketRef = useRef<HTMLDivElement>(null);
  const batteryLifeAndCycleRef = useRef<HTMLDivElement>(null);
  const infoCardRef = useRef<HTMLDivElement>(null);
  const pnlTableRef = useRef<HTMLDivElement>(null);
  const cashFlowTableRef = useRef<HTMLDivElement>(null);
  const parametersRef = useRef<HTMLDivElement>(null);
  const pdfBlobRef = useRef<Blob | null>(null);
  const processingRef = useRef<boolean>(false);
  const abortControllerRef = useRef<AbortController | null>(null);
  const energyFlowAnalysisRef = useRef<HTMLDivElement>(null);

  const { currentLanguage, changeLanguage } = useLanguage();
  const originalLanguageRef = useRef<string>(currentLanguage);

  const [state, setState] = useState({
    chartData: [] as ComparisonChartData[],
    bestSimulation: null as SimulationResult | null,
    closedDataKeys: [] as string[],
    isLoading: true,
    isGeneratingReport: false,
    previewUrl: null as string | null,
    isPreviewModalVisible: false,
    shouldRenderComponents: false,
  });

  const [formValues, setFormValues] = useState({
    selectedSimulations: [] as SimulationResult[],
    selectedMetric: '',
    selectedStorageType: '',
    selectedAssets: [] as string[],
    reportName: '',
    reportTitle: '',
    logoFile: null as File | null,
    language: 'tr' as 'tr' | 'en',
  });

  const [isTabActive, setIsTabActive] = useState(true);

  useEffect(() => {
    const handleVisibilityChange = () => {
      setIsTabActive(!document.hidden);
    };
    const handleFocus = () => setIsTabActive(true);
    const handleBlur = () => setIsTabActive(false);

    document.addEventListener('visibilitychange', handleVisibilityChange);
    window.addEventListener('focus', handleFocus);
    window.addEventListener('blur', handleBlur);

    return () => {
      document.removeEventListener('visibilitychange', handleVisibilityChange);
      window.removeEventListener('focus', handleFocus);
      window.removeEventListener('blur', handleBlur);
    };
  }, []);

  const handleFormChange = useCallback(() => {
    const newValues = {
      selectedSimulations: form.getFieldValue('selectedSimulations') || [],
      selectedMetric: form.getFieldValue('selectedMetric') || '',
      selectedStorageType: form.getFieldValue('selectedStorageType') || '',
      selectedAssets: form.getFieldValue('selectedAssets') || [],
      reportName: form.getFieldValue('reportName') || '',
      reportTitle: form.getFieldValue('reportTitle') || '',
      logoFile: formValues.logoFile,
      language: form.getFieldValue('language') || 'tr',
    };

    setFormValues((prev) => {
      const hasChanged = Object.keys(newValues).some(
        (key) =>
          JSON.stringify(prev[key as keyof typeof prev]) !==
          JSON.stringify(newValues[key as keyof typeof newValues])
      );

      const logoChanged = prev.logoFile !== newValues.logoFile;

      if (hasChanged || logoChanged) {
        pdfBlobRef.current = null;
        return newValues;
      }
      return prev;
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [form, formValues.logoFile, formValues.language, pdfBlobRef]);

  useEffect(() => {
    handleFormChange();
  }, [handleFormChange]);

  const getMetricKey = useCallback(
    (metric: string, storageType: string): keyof SimulationResultDetails => {
      const metricMappings = {
        irr: {
          beforeStorage: 'irrValueWos' as keyof SimulationResultDetails,
          afterStorage: 'irrValue' as keyof SimulationResultDetails,
          storage: 'irrStorage' as keyof SimulationResultDetails,
          default: 'irrValue' as keyof SimulationResultDetails,
        },
        grossProfit: {
          beforeStorage: 'grossProfitWos' as keyof SimulationResultDetails,
          afterStorage: 'grossProfit' as keyof SimulationResultDetails,
          storage: 'grossProfitOfStorage' as keyof SimulationResultDetails,
          default: 'grossProfit' as keyof SimulationResultDetails,
        },
      };
      const mapping = metricMappings[metric as keyof typeof metricMappings];
      return (
        mapping?.[storageType as keyof typeof mapping] ||
        mapping?.default ||
        ('irrValue' as keyof SimulationResultDetails)
      );
    },
    []
  );

  const updateState = useCallback((updates: Partial<typeof state>) => {
    setState((prev) => ({ ...prev, ...updates }));
  }, []);

  const swrKeys = useMemo(() => {
    if (formValues.selectedSimulations.length === 0) return null;
    return formValues.selectedSimulations.map(
      (sim: SimulationResult) => `/simulation/${sim.id}/`
    );
  }, [formValues.selectedSimulations]);

  const { data: simulationDetails, isLoading: isSimLoading } = useSWR<
    SimulationResult[]
  >(
    swrKeys,
    async (urls: string[]) => {
      try {
        updateState({ isLoading: true });
        const results = await Promise.all(
          urls.map(async (url) => {
            try {
              const response = await apiV1.get(url);
              return response || null;
            } catch (error) {
              handleError(error);
              return null;
            }
          })
        );
        return results.filter(
          (result): result is SimulationResult =>
            result !== null &&
            'id' in result &&
            'result' in result &&
            typeof result.result === 'object' &&
            result.result !== null
        );
      } catch (error) {
        console.error('Failed to fetch simulation details:', error);
        return [];
      } finally {
        updateState({ isLoading: false });
      }
    },
    {
      revalidateOnFocus: false,
      shouldRetryOnError: true,
      errorRetryCount: 3,
    }
  );

  const bestSimulationId = useMemo(
    () => state.bestSimulation?.id,
    [state.bestSimulation]
  );
  const bestSimulationPlantId = useMemo(
    () => state.bestSimulation?.plants?.[0]?.id,
    [state.bestSimulation?.plants]
  );

  const hasValidPlant = useMemo(() => {
    return Boolean(
      state.bestSimulation?.plants?.length && state.bestSimulation.plants[0]?.id
    );
  }, [state.bestSimulation?.plants]);

  const { data: marketEnergyActivityData, isLoading: isEnergyActivityLoading } =
    useSWR<SimulationResultMarketEnergyActivity>(
      bestSimulationId
        ? `/simulation/${bestSimulationId}/market_energy_activity/?timeUnit=year`
        : null,
      { revalidateOnFocus: false }
    );

  const { data: generatedGraphData, isLoading: isGeneratedGraphLoading } =
    useSWR<AnnualGenerationGraph>(
      hasValidPlant ? `plant/${bestSimulationPlantId}/generation_graph/` : null
    );

  const { data: dataFlowTotal, isLoading: isDataFlowTotalLoading } =
    useSWR<EnergyFlow>(
      bestSimulationId
        ? `simulation/${bestSimulationId}/energy_flow_total/?timeUnit=year`
        : null,
      { revalidateOnFocus: false }
    );

  const { data: financialData, isLoading: isFinancialDataLoading } =
    useSWR<SimulationResultYearlyFinancialAnalysis>(
      bestSimulationId
        ? `/simulation/${bestSimulationId}/yearly_financial_analysis/`
        : null,
      { revalidateOnFocus: false }
    );

  const { data: batteryDegData, isLoading: isBatteryDataLoading } =
    useSWR<SimulationBatteryDegradation>(
      bestSimulationId
        ? `/simulation/${bestSimulationId}/battery_degradation/`
        : null,
      { revalidateOnFocus: false }
    );

  const uniqueIds = useMemo(
    () => getUniqueIds(simulationDetails || null),
    [simulationDetails]
  );
  const columns = useMemo(
    () => getAfterStorageTableColumns(t, currencySymbol),
    [t, currencySymbol]
  );
  const memoizedTableData = useMemo(
    () => getMemoizedTableData(simulationDetails || null, uniqueIds),
    [simulationDetails, uniqueIds]
  );

  useEffect(() => {
    if (
      !simulationDetails?.length ||
      !formValues.selectedMetric ||
      !formValues.selectedStorageType
    ) {
      if (state.bestSimulation !== null) {
        updateState({ bestSimulation: null });
      }
      return;
    }

    const metricKey = getMetricKey(
      formValues.selectedMetric,
      formValues.selectedStorageType
    );

    const best = simulationDetails.reduce((prev, current) => {
      if (!prev?.result || !current?.result) return prev || current;
      const prevValue = Number(prev.result[metricKey]) || 0;
      const currentValue = Number(current.result[metricKey]) || 0;
      return currentValue > prevValue ? current : prev;
    });

    if (state.bestSimulation?.id !== best?.id) {
      updateState({ bestSimulation: best });
    }
  }, [
    simulationDetails,
    formValues.selectedMetric,
    formValues.selectedStorageType,
    getMetricKey,
    state.bestSimulation,
    updateState,
  ]);

  useEffect(() => {
    if (!simulationDetails?.length || !formValues.selectedMetric) {
      if (state.chartData.length > 0) {
        updateState({ chartData: [] });
      }
      return;
    }

    const data = simulationDetails
      .map((sim) => {
        if (!sim?.result) return null;
        const beforeStorageKey = getMetricKey(
          formValues.selectedMetric,
          'beforeStorage'
        );
        const afterStorageKey = getMetricKey(
          formValues.selectedMetric,
          'afterStorage'
        );
        const storageKey = getMetricKey(formValues.selectedMetric, 'storage');

        return {
          name: sim.name || 'Unnamed Simulation',
          beforeStorage: Number(sim.result[beforeStorageKey]).toFixed(2) || 0,
          afterStorage: Number(sim.result[afterStorageKey]).toFixed(2) || 0,
          storage: Number(sim.result[storageKey]).toFixed(2) || 0,
        };
      })
      .filter((item): item is ComparisonChartData => item !== null);

    if (JSON.stringify(state.chartData) !== JSON.stringify(data)) {
      updateState({ chartData: data });
    }
  }, [
    simulationDetails,
    formValues.selectedMetric,
    getMetricKey,
    state.chartData,
    updateState,
  ]);

  const renderTables = useMemo(() => {
    if (!simulationDetails) return [];
    const tables: JSX.Element[] = [];
    const assetConfigs = [
      { key: 'plant', data: memoizedTableData.plants, label: 'Plant Name' },
      {
        key: 'scenario',
        data: memoizedTableData.scenarios,
        label: 'Scenario Name',
      },
      {
        key: 'storageSystem',
        data: memoizedTableData.storages,
        label: 'Storage Name',
      },
    ];

    assetConfigs.forEach(({ key, data, label }) => {
      if (formValues.selectedAssets.includes(key)) {
        data.forEach((item) => {
          const assetKey = key === 'storageSystem' ? 'storage' : key;
          const asset = item[assetKey as keyof typeof item] as unknown as {
            id: number;
            name: string;
          };
          const filteredSimulations = item.filteredSimulations;
          if (
            asset &&
            typeof asset === 'object' &&
            'id' in asset &&
            'name' in asset
          ) {
            tables.push(
              <div
                key={`${key}-${asset.id}`}
                className="table-container hidden-content"
              >
                <Typography.Title level={4}>
                  {label} - ({String(asset.name)})
                </Typography.Title>
                <Table
                  columns={columns}
                  dataSource={filteredSimulations}
                  scroll={{ x: true }}
                  rowKey="id"
                  pagination={false}
                />
              </div>
            );
          }
        });
      }
    });
    return tables;
  }, [
    formValues.selectedAssets,
    memoizedTableData,
    columns,
    simulationDetails,
  ]);

  useEffect(() => {
    return () => {
      if (state.previewUrl) {
        URL.revokeObjectURL(state.previewUrl);
      }
      if (abortControllerRef.current) {
        abortControllerRef.current.abort();
      }
    };
  }, [state.previewUrl]);

  const chartRefs = useMemo(
    () => [
      generationCardRef,
      mcpChartRef,
      pfcpChartRef,
      sfcpChartRef,
      energyActivityRef,
      energyActivityAtMarketRef,
      batteryLifeAndCycleRef,
      chartRef,
      metricsRef,
      batteryChargeRef,
      batteryDischargeRef,
      incomeBreakdownRef,
      ebitdaStorageRef,
      ebitdaWithStorageRef,
      cashFlowWithStorageRef,
      cashFlowStorageRef,
      energyFlowAnalysisRef,
    ],
    []
  );

  const waitForChartToLoad = useCallback(async (element: HTMLElement) => {
    await new Promise((resolve) => setTimeout(resolve, 2000));

    let retryCount = 0;
    const maxRetries = 10;

    while (retryCount < maxRetries) {
      const rechartsSurface = element.querySelector('.recharts-surface');
      const svgElements = element.querySelectorAll('svg');
      const canvasElements = element.querySelectorAll('canvas');
      const isLoading = element.querySelector('.ant-spin-spinning') !== null;
      const hasNoData = element.querySelector('.ant-empty') !== null;

      const hasChartContent =
        (rechartsSurface && svgElements.length > 0) ||
        canvasElements.length > 0;

      if (isLoading || (!hasChartContent && !hasNoData)) {
        await new Promise((resolve) => setTimeout(resolve, 400));
        retryCount++;
      } else {
        await new Promise((resolve) => setTimeout(resolve, 500));
        break;
      }
    }
  }, []);

  const captureCanvas = useCallback(
    async (element: HTMLElement | null): Promise<string> => {
      if (!element) return '';

      try {
        const originalDisplay = element.style.display;
        const originalVisibility = element.style.visibility;
        const originalPosition = element.style.position;

        element.style.display = 'block';
        element.style.visibility = 'visible';
        element.style.position = 'relative';

        const isChartElement = chartRefs.some((ref) => ref.current === element);

        if (isChartElement) {
          await waitForChartToLoad(element);
        } else {
          await new Promise((resolve) => {
            if (window.requestIdleCallback) {
              window.requestIdleCallback(resolve, { timeout: 100 });
            } else {
              setTimeout(resolve, 50);
            }
          });
        }

        const scale = 3;

        const dataUrl = await toJpeg(element, {
          quality: 1.0,
          backgroundColor: 'white',
          width: element.offsetWidth * scale,
          height: element.offsetHeight * scale,
          style: {
            transform: `scale(${scale})`,
            transformOrigin: 'top left',
          },
        });

        element.style.display = originalDisplay;
        element.style.visibility = originalVisibility;
        element.style.position = originalPosition;
        return dataUrl;
      } catch (error) {
        console.error('Canvas capture error:', error);
        return '';
      }
    },
    [chartRefs, waitForChartToLoad]
  );

  const checkChartReadiness = useCallback(() => {
    const chartElements = document.querySelectorAll('.hidden-content');
    return Array.from(chartElements).every((element) => {
      const rechartsSurface = element.querySelector('.recharts-surface');
      const canvasElements = element.querySelectorAll('canvas');
      const svgElements = element.querySelectorAll('svg');
      const isEmpty = element.querySelector('.ant-empty') !== null;

      const hasChartContent =
        (rechartsSurface && svgElements.length > 0) ||
        canvasElements.length > 0;
      return hasChartContent || isEmpty;
    });
  }, []);

  const waitForComponentsToLoad = useCallback(async (): Promise<void> => {
    const maxWaitTime = 8000;
    const checkInterval = 200;
    const startTime = Date.now();

    return new Promise((resolve) => {
      const checkLoading = () => {
        const currentTime = Date.now();
        if (currentTime - startTime > maxWaitTime) {
          console.warn('PDF generation timed out waiting for components.');
          resolve();
          return;
        }

        const isAnyLoading =
          isEnergyActivityLoading ||
          isGeneratedGraphLoading ||
          isDataFlowTotalLoading ||
          isFinancialDataLoading ||
          isBatteryDataLoading;

        const hasSpinners =
          document.querySelectorAll('.ant-spin-spinning').length > 0;
        const chartsReady = checkChartReadiness();

        if (!isAnyLoading && !hasSpinners && chartsReady) {
          setTimeout(resolve, 300);
        } else {
          setTimeout(checkLoading, checkInterval);
        }
      };
      checkLoading();
    });
  }, [
    isEnergyActivityLoading,
    isGeneratedGraphLoading,
    isDataFlowTotalLoading,
    isFinancialDataLoading,
    isBatteryDataLoading,
    checkChartReadiness,
  ]);

  const toggleClosedDataKeys = useCallback(
    (key = '') => {
      updateState({
        closedDataKeys: state.closedDataKeys.includes(key)
          ? state.closedDataKeys.filter((item) => item !== key)
          : [...state.closedDataKeys, key],
      });
    },
    [state.closedDataKeys, updateState]
  );

  const handleModalClose = useCallback(async () => {
    if (state.previewUrl) {
      URL.revokeObjectURL(state.previewUrl);
    }

    await changeLanguage(originalLanguageRef.current);

    updateState({ previewUrl: null, isPreviewModalVisible: false });
  }, [state.previewUrl, updateState, changeLanguage]);

  const handleLogoUpload = useCallback(
    (file: File) => {
      const isImage = file.type.startsWith('image/');
      if (!isImage) {
        message.error(
          t('components.simReport.step3.form.logo.error.imageOnly')
        );
        return false;
      }
      const isLt2M = file.size / 1024 / 1024 < 2;
      if (!isLt2M) {
        message.error(t('components.simReport.step3.form.logo.error.fileSize'));
        return false;
      }
      setFormValues((prev) => ({
        ...prev,
        logoFile: file,
      }));
      pdfBlobRef.current = null;
      return false;
    },
    [t]
  );

  const handleLogoRemove = useCallback(() => {
    setFormValues((prev) => ({
      ...prev,
      logoFile: null,
    }));
    pdfBlobRef.current = null;
  }, []);

  const handleLanguageChange = useCallback(
    (value: 'tr' | 'en') => {
      form.setFieldValue('language', value);
      handleFormChange();
    },
    [form, handleFormChange]
  );

  const createPdfDocument = useCallback(async (): Promise<Blob | null> => {
    if (!state.bestSimulation?.result || processingRef.current) return null;

    try {
      processingRef.current = true;
      abortControllerRef.current = new AbortController();
      updateState({ shouldRenderComponents: true });

      originalLanguageRef.current = currentLanguage;
      await changeLanguage(formValues.language);

      // Bileşenlerin render edilmesi için daha uzun bekleme süresi
      await new Promise((resolve) => setTimeout(resolve, 1000));

      await waitForComponentsToLoad();

      const componentRefs = [
        { ref: chartRef, name: 'chart' },
        { ref: metricsTableRef, name: 'metricsTable' },
        { ref: metricsRef, name: 'metrics' },
        { ref: batteryChargeRef, name: 'batteryCharge' },
        { ref: batteryDischargeRef, name: 'batteryDischarge' },
        { ref: incomeBreakdownRef, name: 'incomeBreakdown' },
        { ref: analysisTableRef, name: 'analysisTable' },
        { ref: ancillaryTableRef, name: 'ancillaryTable' },
        { ref: ebitdaStorageRef, name: 'ebitdaStorage' },
        { ref: ebitdaWithStorageRef, name: 'ebitdaWithStorage' },
        { ref: cashFlowWithStorageRef, name: 'cashFlowWithStorage' },
        { ref: cashFlowStorageRef, name: 'cashFlowStorage' },
        { ref: beforeStorageTableRef, name: 'beforeStorageTable' },
        { ref: generationCardRef, name: 'generationCard' },
        { ref: mcpChartRef, name: 'mcpChart' },
        { ref: pfcpChartRef, name: 'pfcpChart' },
        { ref: sfcpChartRef, name: 'sfcpChart' },
        { ref: energyActivityRef, name: 'energyActivity' },
        { ref: energyActivityAtMarketRef, name: 'energyActivityAtMarket' },
        { ref: batteryLifeAndCycleRef, name: 'batteryLifeAndCycle' },
        { ref: infoCardRef, name: 'infoCard' },
        { ref: pnlTableRef, name: 'pnlTable' },
        { ref: cashFlowTableRef, name: 'cashFlowTable' },
        { ref: parametersRef, name: 'parameters' },
        { ref: energyFlowAnalysisRef, name: 'energyFlowAnalysis' },
      ];

      const capturePromises = componentRefs.map(async ({ ref, name }) => {
        const imageData = await captureCanvas(ref.current);
        return { name, imageData };
      });

      const imageResults = await Promise.all(capturePromises).then((results) =>
        results.reduce(
          (acc, { name, imageData }) => {
            acc[name] = imageData;
            return acc;
          },
          {} as { [key: string]: string }
        )
      );

      const tableElements = document.querySelectorAll('.table-container');
      const tablePromises = Array.from(tableElements).map((element) =>
        captureCanvas(element as HTMLElement)
      );
      const tableImages = (await Promise.all(tablePromises)).filter(
        (img) => img
      );

      updateState({ shouldRenderComponents: false });

      let logoData = '';
      if (formValues.logoFile) {
        logoData = await new Promise<string>((resolve) => {
          const reader = new FileReader();
          reader.onload = () => resolve(reader.result as string);
          reader.readAsDataURL(formValues.logoFile!);
        });
      }

      const { plants, scenario, storage } = state.bestSimulation;
      const [plantResponse, storageResponse, scenarioResponse] =
        await Promise.allSettled([
          hasValidPlant
            ? apiV1.get(`/plant/${plants[0]?.id}/`)
            : Promise.resolve(null),
          apiV1.get(`/storage/${storage?.id}/`),
          apiV1.get(`/scenario/${scenario?.id}/`),
        ]);

      const reportData: ReportData = {
        reportName: formValues.reportName,
        reportTitle: formValues.reportTitle,
        selectedMetric: formValues.selectedMetric,
        language: formValues.language,
        chartImageData: imageResults.chart,
        metricsTableData: imageResults.metricsTable,
        metricsData: imageResults.metrics,
        batteryChargeData: imageResults.batteryCharge,
        batteryDischargeData: imageResults.batteryDischarge,
        incomeBreakdownData: imageResults.incomeBreakdown,
        analysisTableData: imageResults.analysisTable,
        ancillaryTableData: imageResults.ancillaryTable,
        ebitdaStorageData: imageResults.ebitdaStorage,
        ebitdaWithStorageData: imageResults.ebitdaWithStorage,
        cashFlowWithStorageData: imageResults.cashFlowWithStorage,
        cashFlowStorageData: imageResults.cashFlowStorage,
        generationCardData: imageResults.generationCard,
        mcpChartData: imageResults.mcpChart,
        pfcpChartData: imageResults.pfcpChart,
        sfcpChartData: imageResults.sfcpChart,
        beforeStorageTableData: imageResults.beforeStorageTable,
        energyActivityData: imageResults.energyActivity,
        energyActivityAtMarketData: imageResults.energyActivityAtMarket,
        batteryLifeAndCycleData: imageResults.batteryLifeAndCycle,
        infoCardData: imageResults.infoCard,
        pnlTableData: imageResults.pnlTable,
        cashFlowTableData: imageResults.cashFlowTable,
        parametersData: imageResults.parameters,
        tableImages: tableImages,
        logoData: logoData,
        energyFlowAnalysisData: imageResults.energyFlowAnalysis,
        plantData:
          plantResponse.status === 'fulfilled' ? plantResponse.value : null,
        storageData:
          storageResponse.status === 'fulfilled' ? storageResponse.value : null,
        scenarioData:
          scenarioResponse.status === 'fulfilled'
            ? scenarioResponse.value
            : null,
      };

      const pdfBlob = await pdf(
        <SimulationReportDocument
          reportData={reportData}
          bestSimulation={state.bestSimulation}
          dataFlowTotal={dataFlowTotal!}
          financialData={financialData!}
          batteryDegData={batteryDegData!}
          generatedGraphData={generatedGraphData!}
          marketEnergyActivityData={marketEnergyActivityData!}
          simulationNumber={formValues.selectedSimulations.length}
          selectedSimulations={formValues.selectedSimulations}
          language={formValues.language}
          customization={customization}
          currencySymbol={currencySymbol}
        />
      ).toBlob();

      await changeLanguage(originalLanguageRef.current);

      return pdfBlob;
    } catch (error) {
      console.error('PDF creation error:', error);
      updateState({ shouldRenderComponents: false });
      await changeLanguage(originalLanguageRef.current);
      return null;
    } finally {
      processingRef.current = false;
      abortControllerRef.current = null;
    }
  }, [
    state.bestSimulation,
    formValues,
    captureCanvas,
    dataFlowTotal,
    financialData,
    batteryDegData,
    generatedGraphData,
    marketEnergyActivityData,
    updateState,
    waitForComponentsToLoad,
    hasValidPlant,
    currentLanguage,
    changeLanguage,
    customization,
    currencySymbol,
  ]);

  const previewPDF = useCallback(async () => {
    if (!state.bestSimulation) return;
    try {
      updateState({ isGeneratingReport: true });

      if (pdfBlobRef.current) {
        if (state.previewUrl) URL.revokeObjectURL(state.previewUrl);
        const url = URL.createObjectURL(pdfBlobRef.current);
        updateState({
          previewUrl: url,
          isPreviewModalVisible: true,
          isGeneratingReport: false,
        });
        return;
      }

      setTimeout(async () => {
        const pdfBlob = await createPdfDocument();
        if (pdfBlob) {
          pdfBlobRef.current = pdfBlob;
          if (state.previewUrl) URL.revokeObjectURL(state.previewUrl);
          const url = URL.createObjectURL(pdfBlob);
          updateState({
            previewUrl: url,
            isPreviewModalVisible: true,
            isGeneratingReport: false,
          });
        }
      }, 100);
    } catch (error) {
      console.error('PDF preview error:', error);
      updateState({ isGeneratingReport: false });
    }
  }, [state.bestSimulation, state.previewUrl, updateState, createPdfDocument]);

  const isLoadingAny = useMemo(
    () =>
      state.isLoading ||
      isSimLoading ||
      (state.bestSimulation &&
        (isEnergyActivityLoading ||
          (hasValidPlant && isGeneratedGraphLoading) ||
          isDataFlowTotalLoading ||
          isFinancialDataLoading ||
          isBatteryDataLoading)),
    [
      state.isLoading,
      state.bestSimulation,
      isSimLoading,
      isEnergyActivityLoading,
      hasValidPlant,
      isGeneratedGraphLoading,
      isDataFlowTotalLoading,
      isFinancialDataLoading,
      isBatteryDataLoading,
    ]
  );

  if (isLoadingAny) {
    return (
      <Flex justify="center" align="center" className="h-100">
        <AnimatedText text={t('components.simReport.step3.loading')} />
      </Flex>
    );
  }

  return (
    <Flex vertical className="create-report">
      {state.isGeneratingReport && (
        <Modal
          open={true}
          closable={false}
          footer={null}
          centered
          width={600}
          maskClosable={false}
          getContainer={() =>
            document.querySelector('.create-report') || document.body
          }
          className="report-modal"
        >
          <div className="loading-content">
            <div className="spinner-container"></div>{' '}
            <div className="loading-title">
              {t('components.simReport.step3.modal.title')}
            </div>
            <div className="loading-description">
              {t('components.simReport.step3.modal.description')}
            </div>
            {!isTabActive && (
              <div className="background-warning">
                {t('components.simReport.step3.modal.backgroundWarning')}
              </div>
            )}
          </div>
        </Modal>
      )}
      <Row gutter={16} align="middle" style={{ marginBottom: 16 }}>
        <Col flex="1">
          <Form.Item
            name="reportName"
            label={t('components.simReport.step3.form.reportName.label')}
            rules={[
              {
                required: true,
                message: t(
                  'components.simReport.step3.form.reportName.requiredMessage'
                ),
              },
            ]}
            style={{ marginBottom: 0 }}
          >
            <Input
              placeholder={t(
                'components.simReport.step3.form.reportName.placeholder'
              )}
              onChange={(e) => {
                form.setFieldValue('reportName', e.target.value);
                handleFormChange();
              }}
            />
          </Form.Item>
        </Col>
        <Col flex="1">
          <Form.Item
            name="reportTitle"
            label={t('components.simReport.step3.form.reportTitle.label')}
            rules={[
              {
                required: true,
                message: t(
                  'components.simReport.step3.form.reportTitle.requiredMessage'
                ),
              },
            ]}
            style={{ marginBottom: 0 }}
          >
            <Input
              placeholder={t(
                'components.simReport.step3.form.reportTitle.placeholder'
              )}
              onChange={(e) => {
                form.setFieldValue('reportTitle', e.target.value);
                handleFormChange();
              }}
            />
          </Form.Item>
        </Col>
        <Col flex="1">
          <Form.Item
            name="logo"
            label={t('components.simReport.step3.form.logo.label')}
            style={{ marginBottom: 0 }}
          >
            <Upload
              beforeUpload={handleLogoUpload}
              onRemove={handleLogoRemove}
              maxCount={1}
              accept="image/*"
              listType="picture"
              showUploadList={{
                showPreviewIcon: true,
                showRemoveIcon: true,
              }}
              style={{ position: 'relative' }}
            >
              <Button>
                {t('components.simReport.step3.form.logo.uploadButton')}
              </Button>
            </Upload>
          </Form.Item>
        </Col>
        <Col flex="150px">
          <Form.Item
            name="language"
            label={t('components.simReport.step3.form.language.label')}
            initialValue="tr"
            style={{ marginBottom: 0 }}
          >
            <Select
              placeholder={t(
                'components.simReport.step3.form.language.placeholder'
              )}
              onChange={handleLanguageChange}
              options={[
                {
                  value: 'tr',
                  label: (
                    <div
                      style={{
                        display: 'flex',
                        alignItems: 'center',
                        gap: 8,
                      }}
                    >
                      <ReactCountryFlag
                        countryCode="TR"
                        svg
                        style={{ width: 16, height: 12 }}
                      />
                      <span>
                        {t('components.simReport.step3.form.language.turkish')}
                      </span>
                    </div>
                  ),
                },
                {
                  value: 'en',
                  label: (
                    <div
                      style={{
                        display: 'flex',
                        alignItems: 'center',
                        gap: 8,
                      }}
                    >
                      <ReactCountryFlag
                        countryCode="GB"
                        svg
                        style={{ width: 16, height: 12 }}
                      />
                      <span>
                        {t('components.simReport.step3.form.language.english')}
                      </span>
                    </div>
                  ),
                },
              ]}
            />
          </Form.Item>
        </Col>
        <Col>
          <Form.Item style={{ marginBottom: 0 }}>
            <Button
              onClick={previewPDF}
              disabled={
                !formValues.reportName ||
                !state.bestSimulation ||
                state.isLoading ||
                state.isGeneratingReport
              }
              loading={state.isGeneratingReport}
              type="primary"
              className="mt-32"
            >
              {t('components.simReport.step3.preview.button')}
            </Button>
          </Form.Item>
        </Col>
      </Row>
      <Row id="simulation-chart" ref={chartRef}>
        <ComparisonChart
          chartData={state.chartData}
          selectedMetric={formValues.selectedMetric}
          closedDataKeys={state.closedDataKeys}
          toggleClosedDataKeys={toggleClosedDataKeys}
        />
      </Row>
      {state.shouldRenderComponents && state.bestSimulation && (
        <div className="hidden-container">
          <Row ref={metricsTableRef} gutter={[24, 24]} style={{ width: 1800 }}>
            <Flex vertical gap={8} className="w-100 ">
              <Typography.Title level={3} className="mb-0 ml-12">
                {formValues.language === 'tr'
                  ? 'Depolama Öncesi (Santral):'
                  : 'Without Storage (Plant):'}
              </Typography.Title>
              <Col
                span={24}
                className="hidden-content"
                style={{ marginBottom: 12 }}
              >
                <KeyMetricsTable data={state.bestSimulation} activeTab={0} />
              </Col>
            </Flex>
            <Flex vertical gap={8} className="w-100 ">
              <Typography.Title level={3} className="mb-0 ml-12">
                {formValues.language === 'tr'
                  ? 'Depolama Sonrası (Santral + Depolama):'
                  : 'With Storage (Plant + Storage):'}
              </Typography.Title>
              <Col
                span={24}
                className="hidden-content"
                style={{ marginBottom: 12 }}
              >
                <KeyMetricsTable data={state.bestSimulation} activeTab={1} />
              </Col>
            </Flex>
            <Flex vertical gap={8} className="w-100">
              <Typography.Title level={3} className="mb-0 ml-12">
                {formValues.language === 'tr'
                  ? 'Depolama (Sadece Depolama):'
                  : 'Storage Only:'}
              </Typography.Title>
              <Col
                span={24}
                className="hidden-content"
                style={{ marginBottom: 12 }}
              >
                <KeyMetricsTable data={state.bestSimulation} activeTab={2} />
              </Col>
            </Flex>
          </Row>
          <Row
            ref={energyFlowAnalysisRef}
            className="hidden-content"
            style={{ width: 1800 }}
          >
            <AnnualEnergyFlow simulationId={state.bestSimulation.id} />
          </Row>
          <Row
            ref={beforeStorageTableRef}
            className="hidden-content"
            style={{ width: 1800 }}
          >
            <Table
              columns={getBeforeStorageTableColumns(t, currencySymbol)}
              dataSource={simulationDetails}
              scroll={{ x: true }}
              rowKey="id"
              pagination={false}
            />
          </Row>
          <Row
            ref={metricsRef}
            className="hidden-content"
            style={{ width: 1800 }}
          >
            <KeyMetrics data={state.bestSimulation} />
          </Row>
          <Row
            ref={batteryChargeRef}
            className="hidden-content"
            style={{ width: 1100 }}
          >
            <BatteryFlowForCharge simulationId={state.bestSimulation.id} />
          </Row>
          <Row
            ref={batteryDischargeRef}
            className="hidden-content"
            style={{ width: 1100 }}
          >
            <BatteryFlowForDischarge simulationId={state.bestSimulation.id} />
          </Row>
          <Row
            ref={incomeBreakdownRef}
            className="hidden-content"
            style={{ width: 1800 }}
          >
            <IncomeBreakdown
              simulationId={state.bestSimulation.id}
              activeTab={2}
            />
          </Row>
          <Row
            ref={analysisTableRef}
            className="hidden-content"
            style={{ width: 1800 }}
          >
            <AnalysisTable
              showNumbers={true}
              simulationId={state.bestSimulation.id}
              activeTab={1}
            />
          </Row>
          <Row
            ref={ancillaryTableRef}
            className="hidden-content"
            style={{ width: 3000 }}
          >
            <AnalysisTable
              simulationId={state.bestSimulation.id}
              activeTab={2}
            />
          </Row>
          <Row
            ref={ebitdaWithStorageRef}
            className="hidden-content"
            style={{ width: 1800 }}
          >
            <Ebitda
              simulationId={state.bestSimulation.id}
              activeTab="withStorage"
            />
          </Row>
          <Row
            ref={ebitdaStorageRef}
            className="hidden-content"
            style={{ width: 1800 }}
          >
            <Ebitda
              simulationId={state.bestSimulation.id}
              activeTab="storage"
            />
          </Row>
          <Row
            ref={cashFlowWithStorageRef}
            className="hidden-content"
            style={{ width: 1800 }}
          >
            <CashFlow
              simulationId={state.bestSimulation.id}
              activeTab="withStorage"
              activeButton={1}
            />
          </Row>
          <Row
            ref={cashFlowStorageRef}
            className="hidden-content"
            style={{ width: 1800 }}
          >
            <CashFlow
              simulationId={state.bestSimulation.id}
              activeTab="storage"
              activeButton={1}
            />
          </Row>
          <Row className="hidden-content" style={{ width: 1800 }}>
            <Space direction="vertical" className="w-100 mt-24">
              {renderTables}
            </Space>
          </Row>
          <Row
            ref={generationCardRef}
            className="hidden-content"
            style={{ width: 1800 }}
          >
            {hasValidPlant && (
              <GenerationCard
                currentPlantId={state.bestSimulation?.plants[0]?.id}
              />
            )}
          </Row>
          <Row
            ref={mcpChartRef}
            className="hidden-content energy-market pdf-capture"
            style={{ width: 1800 }}
          >
            <EnergyActivityAtMarket
              simulationId={state.bestSimulation.id}
              closedDataKeys={[
                'energySold',
                'energyPurchased',
                'pfcParticipationAmount',
                'sfcParticipationAmount',
                'pfcPrice',
                'sfcPrice',
              ]}
            />
          </Row>
          <Row
            ref={pfcpChartRef}
            className="hidden-content energy-market pdf-capture"
            style={{ width: 1800 }}
          >
            <EnergyActivityAtMarket
              simulationId={state.bestSimulation.id}
              closedDataKeys={[
                'energySold',
                'energyPurchased',
                'pfcParticipationAmount',
                'sfcParticipationAmount',
                'sfcPrice',
                'mcpPrice',
              ]}
            />
          </Row>
          <Row
            ref={sfcpChartRef}
            className="hidden-content energy-market pdf-capture"
            style={{ width: 1800 }}
          >
            <EnergyActivityAtMarket
              simulationId={state.bestSimulation.id}
              closedDataKeys={[
                'energySold',
                'energyPurchased',
                'pfcParticipationAmount',
                'sfcParticipationAmount',
                'pfcPrice',
                'mcpPrice',
              ]}
            />
          </Row>
          <Row
            ref={energyActivityRef}
            className="hidden-content"
            style={{ width: 1800 }}
          >
            <EnergyActivity
              simulationId={state.bestSimulation.id}
              showTooltip
            />
          </Row>
          <Row
            ref={energyActivityAtMarketRef}
            className="hidden-content energy-market pdf-capture"
            style={{ width: 1800 }}
          >
            <EnergyActivityAtMarket
              simulationId={state.bestSimulation.id}
              filterProps={{
                startDate: new Date(state.bestSimulation.startDate),
                timeUnit: 'hour',
                inputValue: 24,
              }}
              showTooltip
            />
          </Row>
          <Row
            ref={batteryLifeAndCycleRef}
            className="hidden-content"
            style={{ width: 1800 }}
          >
            <BatteryAndLifeCycle
              simulationId={state.bestSimulation.id}
              showTooltip
            />
          </Row>
          <Row
            ref={infoCardRef}
            className="hidden-content"
            style={{ width: 500 }}
          >
            <InfoCard simulationId={state.bestSimulation.id} />
          </Row>
          <Row
            ref={pnlTableRef}
            className="hidden-content"
            style={{ width: 3200 }}
          >
            <ProfitAndLossTableCompact simulationId={state.bestSimulation.id} />
          </Row>
          <Row
            ref={cashFlowTableRef}
            className="hidden-content"
            style={{ width: 3000 }}
          >
            <CashFlowTable simulationId={state.bestSimulation.id} />
          </Row>
          <Row
            ref={parametersRef}
            className="hidden-content parameters"
            style={{ width: 1800 }}
          >
            <Parameters simulationData={state.bestSimulation} />
          </Row>
        </div>
      )}
      <Modal
        title={t('components.simReport.step3.preview.modal.title')}
        open={state.isPreviewModalVisible}
        onCancel={handleModalClose}
        width="80%"
        style={{ top: 20 }}
        footer={[
          <Button key="back" onClick={handleModalClose}>
            {t('components.simReport.step3.preview.modal.close')}
          </Button>,
          <Button
            key="download"
            type="primary"
            onClick={() => {
              if (state.previewUrl) {
                const link = document.createElement('a');
                link.href = state.previewUrl;
                link.download = `${formValues.reportName}.pdf`;
                link.click();
              }
            }}
          >
            {t('components.simReport.step3.preview.modal.download')}
          </Button>,
        ]}
      >
        {state.previewUrl && (
          <iframe
            src={state.previewUrl}
            style={{ width: '100%', height: '80vh', border: 'none' }}
            title="PDF Preview"
          />
        )}
      </Modal>
    </Flex>
  );
};

export default CreateReport;
